import { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_LOGO } from "../lib/brand";
import { Power, QrCode, ListChecks } from "@phosphor-icons/react";

/**
 * MerchantDashboard — simplified scope.
 * Only two surfaces are exposed to merchant_owner / cashier:
 *   1. QR Scanner (the actual /scanner route, opened in its own page for mobile UX)
 *   2. Transaction History — privacy mode (date, time, member name only)
 * All other admin/merchant features are deliberately hidden.
 */

const Sidebar = ({ logout, name, role }) => (
  <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/5 bg-[#070707]">
    <div className="p-5 flex items-center gap-3 border-b border-white/5">
      <img src={BRAND_LOGO} alt="" className="h-10 w-10 rounded-full ring-1 ring-[#D4AF37]/40" />
      <div>
        <p className="font-display text-lg text-white leading-tight">{role === "cashier" ? "Cashier" : "Merchant"}</p>
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Verification</p>
      </div>
    </div>
    <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto" data-testid="merchant-sidebar">
      <NavLink
        to="/scanner"
        className="flex items-center gap-2 px-4 py-2.5 rounded-md text-[11px] tracking-luxe uppercase whitespace-nowrap bg-gold-gradient text-black font-semibold"
        data-testid="merchant-nav-scanner"
      >
        <QrCode size={18} weight="bold" /> Open Scanner
      </NavLink>
      <NavLink
        to="/merchant/transactions"
        className={({ isActive }) =>
          `flex items-center gap-2 px-4 py-2.5 rounded-md text-[11px] tracking-luxe uppercase whitespace-nowrap ${isActive ? "bg-[#0d0d0d] text-[#D4AF37] border border-[#D4AF37]/30" : "text-white/65 hover:text-white"}`
        }
        data-testid="merchant-nav-transactions"
      >
        <ListChecks size={18} /> Transaction History
      </NavLink>
    </nav>
    <div className="p-4 lg:mt-auto border-t border-white/5 hidden lg:flex items-center justify-between">
      <span className="text-[10px] tracking-luxe uppercase text-white/40">{name}</span>
      <button onClick={logout} className="text-white/60 hover:text-white" data-testid="merchant-logout"><Power size={18} /></button>
    </div>
  </aside>
);

const formatDateLabel = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
};
const formatTimeLabel = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

function TransactionsLite() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/merchant/transactions-lite").then(({ data }) => setList(data)).finally(() => setLoading(false));
  }, []);
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Transaction History</p>
      <h1 className="font-display text-4xl text-white mt-2">QR usage log</h1>
      <p className="text-white/55 text-sm mt-2 max-w-xl">
        Date, time and member name only. Financial reporting is handled by Admin.
      </p>
      {loading ? (
        <p className="text-white/55 italic mt-8">Loading…</p>
      ) : list.length === 0 ? (
        <div className="tsc-card mt-8 p-10 text-center" data-testid="merchant-tx-empty">
          <ListChecks size={28} className="text-white/30 mx-auto" weight="duotone" />
          <p className="text-white/60 mt-3">No QR scans yet.</p>
          <p className="text-white/35 text-xs mt-1">Once a member presents their QR, the visit will appear here.</p>
        </div>
      ) : (
        <div className="tsc-card mt-8 divide-y divide-white/5" data-testid="merchant-tx-list">
          <div className="grid grid-cols-12 px-5 py-3 text-[10px] tracking-luxe uppercase text-white/40">
            <p className="col-span-4">Date</p>
            <p className="col-span-3">Time</p>
            <p className="col-span-5">Member</p>
          </div>
          {list.map((t, i) => (
            <div key={i} className="grid grid-cols-12 px-5 py-3 text-sm items-center" data-testid={`merchant-tx-row-${i}`}>
              <p className="col-span-4 text-white/85">{formatDateLabel(t.created_at)}</p>
              <p className="col-span-3 text-white/70">{formatTimeLabel(t.created_at)}</p>
              <p className="col-span-5 text-white" data-testid={`merchant-tx-name-${i}`}>{t.member_name}</p>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function MerchantDashboard() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  // After login, merchant role goes straight to /scanner — but a user landing on
  // /merchant directly should also be funneled to the scanner by default.
  useEffect(() => {
    if (window.location.pathname === "/merchant" || window.location.pathname === "/merchant/") {
      nav("/scanner", { replace: true });
    }
  }, [nav]);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col lg:flex-row">
      <Sidebar logout={logout} name={user?.name} role={user?.role} />
      <main className="flex-1 p-6 sm:p-10 max-w-5xl">
        <Routes>
          <Route index element={<Navigate to="/scanner" replace />} />
          <Route path="transactions" element={<TransactionsLite />} />
          <Route path="*" element={<Navigate to="/scanner" replace />} />
        </Routes>
      </main>
    </div>
  );
}
